import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { config } from "../../services/configHeader";

const baseUrl = process.env.REACT_APP_BASE_URI;

// Get user data from localStorage
const getUserData = () => {
    try {
        return JSON.parse(localStorage.getItem("user_data") || "{}");
    } catch (e) {
        console.error("Error parsing user data:", e);
        return {};
    }
};

// Get account type from localStorage
const getAccountType = () => {
    try {
        const userData = getUserData();
        const accountType = userData?.account_type || localStorage.getItem("account_type") || "1";
        return parseInt(accountType) || 1;
    } catch (e) {
        console.error("Error getting account type:", e);
        return 1;
    }
};

// Check if account is view-only (type 2)
const isViewOnlyAccount = () => {
    return getAccountType() === 2;
};

// Initial state
const initialState = {
    // TDS data
    tdsLoading: false,
    tdsSuccess: false,
    tdsError: false,
    tdsMessage: null,
    tdsData: null,

    // TDS status data
    tdsStatusLoading: false,
    tdsStatusSuccess : false,
    tdsStatusError : false,
    tdsStatusMessage: null,
    tdsStatusData: null,

    // TDS all data
    tdsAllLoading: false,
    tdsAllSuccess: false,
    tdsAllError : false,
    tdsAllMessage: null,
    tdsAllData: null,

    // Employee payroll records
    tdsEmpListLoading: false,
    tdsEmpListSuccess: false,
    tdsEmpListError : false,
    tdsEmpListMessage: null,
    tdsEmpListData: null,
    tdsEmpListNormalized: [],

    // TDS Declaration List
    tdsDeclLoading: false,
    tdsDeclSuccess: false,
    tdsDeclError :false,
    tdsDeclMessage: null,
    tdsDeclData: null,

    // TDS General Rules
    tdsGenLoading: false,
    tdsGenSuccess: false,
    tdsGenError : false,
    tdsGenMessage: null,
    tdsGenData: null,

    // TDS Validation
    tdsValiLoading: false,
    tdsValiSuccess: false,
    tdsValiError : false,
    tdsValiMessage: null,
    tdsValiData: null,

    // TDS Notification
    tdsNotiLoading: false,
    tdsNotiSuccess: false,
    tdsNotiError : false,
    tdsNotiMessage: null,
    tdsNotiData: null,

    // TDS Review Queue
    reviewQueueLoading: false,
    reviewQueueSuccess: false,
    reviewQueueError: false,
    reviewQueueMessage: null,
    reviewQueueData: null,
};

// View-only account error response
const getViewOnlyErrorResponse = () => {
    console.log("Returning view-only response - no API call will be made");
    return {
        data: {
            code: 200,
            status: false,
            message: "Sorry, you can only view the data, not create it.",
            data: {
                processedCount: 0,
                totalRequested: 0,
                errors: ["View-only account cannot perform this action"]
            },
        },
    };
};

// Helper function to get user ID - ALWAYS returns a user ID
// Helper function to get user ID - ALWAYS returns a user ID
const getUserId = () => {
    try {
        const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
        
        // Check multiple possible field names
        const possibleIdFields = [
            'user_id', 'userId', 'id', 'employee_id', 'employeeId', 
            'staff_id', 'staffId', 'staff_code'
        ];
        
        for (const field of possibleIdFields) {
            if (userData[field] !== undefined && userData[field] !== null && userData[field] !== '') {
                const idValue = userData[field];
                // Convert to number if possible
                if (!isNaN(idValue) && idValue !== '') {
                    return Number(idValue);
                }
                return idValue;
            }
        }
        
        // Check localStorage directly
        const localStorageUserId = localStorage.getItem('user_id') || 
                                  localStorage.getItem('userId') || 
                                  localStorage.getItem('id');
        
        if (localStorageUserId && localStorageUserId !== '') {
            if (!isNaN(localStorageUserId)) {
                return Number(localStorageUserId);
            }
            return localStorageUserId;
        }
        
        // Last resort - return default
        console.warn("No user ID found, using default value 1");
        return 1;
        
    } catch (error) {
        console.error("Error getting user ID:", error);
        return 1;
    }
};

const prepareRequestData = (data, defaultAction = null) => {
    // Get user ID from localStorage
    const userId = getUserId();
    
    // Get user data to extract staff_id
    const getUserData = () => {
        try {
            return JSON.parse(localStorage.getItem("user_data") || "{}");
        } catch (e) {
            console.error("Error parsing user_data:", e);
            return {};
        }
    };
    
    const userData = getUserData();
    
    // Start with all original data
    const requestData = { ...data };
    
    // CRITICAL: ALWAYS add userId to every request (in both formats)
    requestData.userId = userId;
    requestData.user_id = userId;
    
    // ========== FIXED: ACTION HANDLING ==========
    // For /api/admin/manageTdsForms, action MUST be: approve, reject, hold, request_edit
    // DO NOT override if action is already provided
    
    // Valid actions for manageTdsForms API
    const validManageTdsActions = ["approve", "reject", "hold", "request_edit"];
    
    // Check if this is a manageTdsForms request (by checking if action is one of the valid ones)
    const isManageTdsRequest = data.action && validManageTdsActions.includes(data.action);
    
    if (isManageTdsRequest) {
        // For manageTdsForms: KEEP the original action, don't override
        requestData.action = data.action;
        console.log(`ManageTdsForms request - using provided action: "${requestData.action}"`);
    } else if (requestData.action) {
        // If action is already provided (for other APIs), keep it
        console.log(`Using provided action: "${requestData.action}"`);
    } else if (defaultAction) {
        // Use default action if provided and no action exists
        requestData.action = defaultAction;
        console.log(`Using default action: "${defaultAction}"`);
    } else {
        // Default to "list" for other APIs
        requestData.action = "list";
        console.log(`No action provided, using default: "list"`);
    }
    
    // ========== FIXED: STAFF_ID EXTRACTION ==========
    // CRITICAL: EXTRACT AND ADD STAFF_ID FROM MULTIPLE SOURCES
    return requestData;
};

// ============================================
// UPDATED extractStaffId FUNCTION
// ============================================
const extractStaffId = (state) => {
    console.log('🔍 Extracting staff ID from sources...');
    
    // Debug: Log what's available
    console.log('📦 Redux state available:', !!state);
    console.log('📦 localStorage keys:', Object.keys(localStorage));
    
    // Priority 1: From Redux state (if available)
    if (state?.auth?.user?.staff_id) {
        console.log('✅ Found in state.auth.user.staff_id:', state.auth.user.staff_id);
        return state.auth.user.staff_id;
    }
    if (state?.auth?.user?.staffId) {
        console.log('✅ Found in state.auth.user.staffId:', state.auth.user.staffId);
        return state.auth.user.staffId;
    }
    if (state?.auth?.user?.id) {
        console.log('✅ Found in state.auth.user.id:', state.auth.user.id);
        return state.auth.user.id;
    }
    
    // Priority 2: From tdsUser in localStorage (YOUR ACTUAL SOURCE)
    try {
        const tdsUserStr = localStorage.getItem('tdsUser');
        if (tdsUserStr) {
            const tdsUser = JSON.parse(tdsUserStr);
            console.log('📦 tdsUser found:', tdsUser);
            
            if (tdsUser.staffId) {
                console.log('✅ Found in tdsUser.staffId:', tdsUser.staffId);
                // Also save to other formats for compatibility
                localStorage.setItem('staff_id', tdsUser.staffId);
                localStorage.setItem('staffId', tdsUser.staffId);
                return tdsUser.staffId;
            }
            if (tdsUser.staff_id) {
                console.log('✅ Found in tdsUser.staff_id:', tdsUser.staff_id);
                localStorage.setItem('staff_id', tdsUser.staff_id);
                localStorage.setItem('staffId', tdsUser.staff_id);
                return tdsUser.staff_id;
            }
            if (tdsUser.employeeId) {
                console.log('✅ Found in tdsUser.employeeId:', tdsUser.employeeId);
                localStorage.setItem('staff_id', tdsUser.employeeId);
                localStorage.setItem('staffId', tdsUser.employeeId);
                return tdsUser.employeeId;
            }
        }
    } catch (e) {
        console.warn('Error parsing tdsUser:', e);
    }
    
    // Priority 3: From user_data in localStorage
    try {
        const userDataStr = localStorage.getItem('user_data');
        if (userDataStr) {
            const userData = JSON.parse(userDataStr);
            console.log('📦 user_data found:', userData);
            
            const possibleIds = [
                userData.staff_id,
                userData.staffId,
                userData.employee_id,
                userData.id,
                userData.user_id,
                userData.userId
            ].filter(id => id);
            
            if (possibleIds.length > 0) {
                const staffId = possibleIds[0];
                console.log('✅ Found in user_data:', staffId);
                localStorage.setItem('staff_id', staffId);
                localStorage.setItem('staffId', staffId);
                return staffId;
            }
        }
    } catch (e) {
        console.warn('Error parsing user_data:', e);
    }
    
    // Priority 4: From selectedEmployee in localStorage
    try {
        const selectedEmpStr = localStorage.getItem('selectedEmployee');
        if (selectedEmpStr) {
            const selectedEmp = JSON.parse(selectedEmpStr);
            console.log('📦 selectedEmployee found:', selectedEmp);
            
            if (selectedEmp.staffId) {
                console.log('✅ Found in selectedEmployee.staffId:', selectedEmp.staffId);
                localStorage.setItem('staff_id', selectedEmp.staffId);
                localStorage.setItem('staffId', selectedEmp.staffId);
                return selectedEmp.staffId;
            }
            if (selectedEmp.staff_id) {
                console.log('✅ Found in selectedEmployee.staff_id:', selectedEmp.staff_id);
                localStorage.setItem('staff_id', selectedEmp.staff_id);
                localStorage.setItem('staffId', selectedEmp.staff_id);
                return selectedEmp.staff_id;
            }
        }
    } catch (e) {
        console.warn('Error parsing selectedEmployee:', e);
    }
    
    // Priority 5: From direct localStorage keys
    const directKeys = [
        'staff_id', 'staffId', 'employee_id', 'userId', 'id', 
        'tdsStaffId', 'user_id', 'staff_code', 'employeeId'
    ];
    
    for (const key of directKeys) {
        const value = localStorage.getItem(key);
        if (value) {
            console.log(`✅ Found in localStorage.${key}:`, value);
            return value;
        }
    }
    
    // Priority 6: From sessionStorage
    const sessionKeys = ['staffId', 'tdsStaffId', 'employeeId', 'userId'];
    for (const key of sessionKeys) {
        const value = sessionStorage.getItem(key);
        if (value) {
            console.log(`✅ Found in sessionStorage.${key}:`, value);
            return value;
        }
    }
    
    console.error("❌ COULD NOT FIND STAFF_ID IN ANY SOURCE");
    return null;
};


// Save TDS Status Data - WITH ALL REQUIRED FIELDS
export const saveTdsStatusData = createAsyncThunk(
    "saveTdsStatusData",
    async (body) => {
        try {
            const { path, data } = body;
            const url = baseUrl + path;
            
            console.log("=== SAVE TDS STATUS API ===");
            console.log("API URL:", url);
            console.log("Received data:", JSON.stringify(data, null, 2));
            
            // Check if account is view-only
            if (isViewOnlyAccount()) {
                console.warn("View-only account detected - returning mock data");
                return getViewOnlyErrorResponse();
            }
            
            // ========== REMOVE THE PRE-VALIDATION ==========
            // Let the server handle validation, not us
            // The server will return proper error if action is invalid
            
            // Just prepare the request data
            const requestData = prepareRequestData(data, data?.action || "approve");
            
            console.log("=== MAKING STATUS UPDATE API CALL ===");
            console.log("URL:", url);
            console.log("Final Request Data:", JSON.stringify(requestData, null, 2));
            console.log("Action being sent:", requestData.action);
            
            // Make the API call - let server validate
            const response = await axios.post(url, requestData, config);
            
            console.log("API Response received:", response.data);
            return response;
            
        } catch (error) {
            console.error("saveTdsStatusData API Error:", error);
            
            if (error.response) {
                console.error("Error Response Data:", error.response.data);
                console.error("Error Status:", error.response.status);
                return {
                    response: {
                        data: error.response.data,
                        status: error.response.status,
                        statusText: error.response.statusText
                    }
                };
            }
            
            if (!navigator.onLine) {
                return {
                    response: {
                        data: {
                            code: 500,
                            status: false,
                            message: "No Internet Connection Found",
                            data: null
                        }
                    }
                };
            }
            
            return {
                response: {
                    data: {
                        code: 500,
                        status: false,
                        message: error.message || "Request failed",
                        data: null
                    }
                }
            };
        }
    }
);
// Get TDS All Data - WITH ALL REQUIRED FIELDS
export const getTdsAllData = createAsyncThunk(
    "getTdsAllData",
    async (body) => {
        try {
            const { path, data } = body;
            const url = baseUrl + path;
            
            // Check if account is view-only
            if (isViewOnlyAccount()) {
                console.log("View-only account detected, returning mock data");
                return getViewOnlyErrorResponse();
            }
            
            // Build the exact payload required by the dashboard API
            const requestData = {
                userId: data?.userId ?? data?.user_id ?? getUserId(),
                financialYear: data?.financialYear ?? null,
                departmentId: data?.departmentId ?? null,
                branchId: data?.branchId ?? null,
                regimeType: data?.regimeType === "old" ? null : (data?.regimeType ?? null),
                // Optional query controls for dashboard (search, sorting, pagination)
                search: data?.search ?? "",
                sortBy: data?.sortBy ?? "name",
                page: data?.page ?? 1,
                limit: data?.limit ?? 100,
            };

            console.log("Making API call for getTdsAllData");
            console.log("Request data:", requestData);
            const response = await axios.post(url, requestData, config);
            return response;
            
        } catch (error) {
            console.error("getTdsAllData API Error:", error);
            
            if (error.response) {
                return {
                    response: {
                        data: error.response.data
                    }
                };
            }
            
            if (!navigator.onLine) {
                return {
                    response: {
                        data: {
                            code: 500,
                            status: false,
                            message: "No Internet Connection Found",
                            data: []
                        }
                    }
                };
            }
            
            return {
                response: {
                    data: {
                        code: 500,
                        status: false,
                        message: error.message || "Request failed",
                        data: []
                    }
                }
            };
        }
    }
);

// Get TDS Employee List Data - WITH ALL REQUIRED FIELDS
export const getTdsEmpListData = createAsyncThunk(
    "getTdsEmpListData",
    async (body) => {
        try {
            const { path, data } = body;
            const url = baseUrl + path;
            
            if (isViewOnlyAccount()) {
                return getViewOnlyErrorResponse();
            }
            
            const requestData = { ...data };
            if (requestData.userId === undefined && requestData.user_id !== undefined) {
                requestData.userId = requestData.user_id;
            }
            if (requestData.userId === undefined) {
                requestData.userId = getUserId();
            }

            // Enforce exact request shape for employee list payload
            delete requestData.user_id;

            console.log("getTdsEmpListData request:", requestData);
            
            const response = await axios.post(url, requestData, config);
            // Return full axios response so reducers can uniformly parse formats
            return response;
            
        } catch (error) {
            console.error("getTdsEmpListData API Error:", error);
            
            if (error.response) {
                return {
                    response: {
                        data: error.response.data
                    }
                };
            }
            
            if (!navigator.onLine) {
                return {
                    response: {
                        data: {
                            code: 500,
                            status: false,
                            message: "No Internet Connection Found",
                            data: []
                        }
                    }
                };
            }
            
            return {
                response: {
                    data: {
                        code: 500,
                        status: false,
                        message: error.message || "Request failed",
                        data: []
                    }
                }
            };
        }
    }
);

// Get TDS Declaration List Data - WITH ALL REQUIRED FIELDS
export const getTdsDecListData = createAsyncThunk(
    "getTdsDecListData",
    async (body) => {
        try {
            const { path, data } = body;
            const url = baseUrl + path;
            
            console.log("=== GET TDS DECLARATION LIST API ===");
            console.log("API URL:", url);
            
            if (isViewOnlyAccount()) {
                return getViewOnlyErrorResponse();
            }
            
            // Prepare request data with ALL required fields
            const requestData = prepareRequestData(data, "list");
            
            console.log("Final Request Data for getTdsDecListData:", JSON.stringify(requestData, null, 2));
            
            // Validate staff_id is present before making API call
            if (!requestData.staff_id && !requestData.staffId && !requestData.staffids) {
                console.error("CRITICAL ERROR: No staff_id found after prepareRequestData");
                
                // Try to get from localStorage as fallback
                const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
                console.error("Current user_data:", userData);
                
                return {
                    response: {
                        data: {
                            code: 400,
                            status: false,
                            message: "Staff ID is required but could not be found. Please check user data.",
                            data: []
                        }
                    }
                };
            }
            
            const response = await axios.post(url, requestData, config);
            return response.data;
            
        } catch (error) {
            console.error("getTdsDecListData API Error:", error);
            
            if (error.response) {
                console.error("Error Response:", error.response.data);
                
                // Check if it's the staff_id error
                if (error.response.data?.message?.includes("Staff ID is required")) {
                    console.error("STAFF ID ERROR DETECTED!");
                    
                    // Use body.data instead of just data
                    console.error("What we sent:", {
                        staff_id: body?.data?.staff_id,
                        staffId: body?.data?.staffId,
                        staffids: body?.data?.staffids
                    });
                    
                    // Debug localStorage
                    console.error("LocalStorage user_data:", 
                        JSON.parse(localStorage.getItem("user_data") || "{}"));
                }
                
                return {
                    response: {
                        data: error.response.data
                    }
                };
            }
            
            if (!navigator.onLine) {
                return {
                    response: {
                        data: {
                            code: 500,
                            status: false,
                            message: "No Internet Connection Found",
                            data: []
                        }
                    }
                };
            }
            
            return {
                response: {
                    data: {
                        code: 500,
                        status: false,
                         message: error.message || "Request failed",
                        data: []
                    }
                }
            };
        }
    }
);
// Helper function to get staff_id from alternative sources
const getAlternativeStaffId = () => {
    try {
        // Check localStorage for any staff-related keys
        const keysToCheck = [
            'staff_id', 'staffId', 'staff_code', 'staffCode',
            'employee_id', 'employeeId', 'userId', 'user_id',
            'id'
        ];
        
        for (const key of keysToCheck) {
            const value = localStorage.getItem(key);
            if (value && !isNaN(value)) {
                console.log(`Found staff_id in localStorage.${key}:`, value);
                return Number(value);
            }
        }
        
        // Check user_data object
        const userDataStr = localStorage.getItem('user_data');
        if (userDataStr) {
            const userData = JSON.parse(userDataStr);
            const userDataKeys = ['staff_id', 'staffId', 'staff_code', 'employee_id', 'id', 'user_id'];
            
            for (const key of userDataKeys) {
                if (userData[key] && !isNaN(userData[key])) {
                    console.log(`Found staff_id in user_data.${key}:`, userData[key]);
                    return Number(userData[key]);
                }
            }
        }
        
        console.warn("No alternative staff_id found");
        return null;
        
    } catch (error) {
        console.error("Error getting alternative staff_id:", error);
        return null;
    }
};

// Save TDS Data - WITH ALL REQUIRED FIELDS
export const tdsSaveData = createAsyncThunk(
    "tdsSaveData",
    async (body) => {
        try {
            const { path, data } = body;
            const url = baseUrl + path;
            
            if (isViewOnlyAccount()) {
                return getViewOnlyErrorResponse();
            }
            
            // Prepare request data with ALL required fields
            const requestData = prepareRequestData(data, "save");
            
            console.log("tdsSaveData request:", requestData);
            
            const response = await axios.post(url, requestData, config);
            return response;
            
        } catch (error) {
            console.error("tdsSaveData API Error:", error);
            
            if (error.response) {
                return {
                    response: {
                        data: error.response.data
                    }
                };
            }
            
            if (!navigator.onLine) {
                return {
                    response: {
                        data: {
                            code: 500,
                            status: false,
                            message: "No Internet Connection Found",
                            data: []
                        }
                    }
                };
            }
            
            return {
                response: {
                    data: {
                        code: 500,
                        status: false,
                        message: error.message || "Request failed",
                        data: []
                    }
                }
            };
        }
    }
);

// Get TDS General Rules
export const getTdsGeneralRules = createAsyncThunk(
    "getTdsGeneralRules",
    async (path) => {
        try {
            if (isViewOnlyAccount()) {
                return getViewOnlyErrorResponse();
            }
            
            const response = await axios.get(`${baseUrl}${path}`, config);
            return response;
            
        } catch (error) {
            console.error("getTdsGeneralRules API Error:", error);
            
            if (error.response) {
                return {
                    response: {
                        data: error.response.data
                    }
                };
            }
            
            if (!navigator.onLine) {
                return {
                    response: {
                        data: {
                            code: 500,
                            status: false,
                            message: "No Internet Connection Found",
                            data: []
                        }
                    }
                };
            }
            
            return {
                response: {
                    data: {
                        code: 500,
                        status: false,
                        message: error.message || "Request failed",
                        data: []
                    }
                }
            };
        }
    }
);

// Get TDS Validation
export const getTdsValidation = createAsyncThunk(
    "getTdsValidation",
    async (path) => {
        try {
            if (isViewOnlyAccount()) {
                return getViewOnlyErrorResponse();
            }
            
            const response = await axios.get(`${baseUrl}${path}`, config);
            return response;
            
        } catch (error) {
            console.error("getTdsValidation API Error:", error);
            
            if (error.response) {
                return {
                    response: {
                        data: error.response.data
                    }
                };
            }
            
            if (!navigator.onLine) {
                return {
                    response: {
                        data: {
                            code: 500,
                            status: false,
                            message: "No Internet Connection Found",
                            data: []
                        }
                    }
                };
            }
            
            return {
                response: {
                    data: {
                        code: 500,
                        status: false,
                        message: error.message || "Request failed",
                        data: []
                    }
                }
            };
        }
    }
);

// Get TDS Notification
export const getTdsNotification = createAsyncThunk(
    "getTdsNotification",
    async (path) => {
        try {
            if (isViewOnlyAccount()) {
                return getViewOnlyErrorResponse();
            }
            
            const response = await axios.get(`${baseUrl}${path}`, config);
            return response;
            
        } catch (error) {
            console.error("getTdsNotification API Error:", error);
            
            if (error.response) {
                return {
                    response: {
                        data: error.response.data
                    }
                };
            }
            
            if (!navigator.onLine) {
                return {
                    response: {
                        data: {
                            code: 500,
                            status: false,
                            message: "No Internet Connection Found",
                            data: []
                        }
                    }
                };
            }
            
            return {
                response: {
                    data: {
                        code: 500,
                        status: false,
                        message: error.message || "Request failed",
                        data: []
                    }
                }
            };
        }
    }
);

// Helper function to extract response data
const extractResponseData = (action) => {
    const payload = action.payload;

    // If returned payload is a raw array, treat it as a successful response
    if (Array.isArray(payload)) {
        return {
            data: payload,
            code: 200,
            status: true,
            message: "",
            success: true,
        };
    }

    // If payload is direct response data object from axios
    if (payload && typeof payload === 'object' && !payload?.response && payload?.data === undefined) {
        const responseData = payload;

        if (responseData.code !== undefined) {
            const isSuccess = responseData.status === true &&
                responseData.code === 200 &&
                (!responseData.data?.errors || responseData.data.errors.length === 0);

            return {
                data: responseData.data,
                code: responseData.code,
                status: responseData.status,
                message: responseData.message,
                success: isSuccess,
            };
        }

        if (responseData.status !== undefined) {
            return {
                data: responseData.data || responseData,
                status: responseData.status,
                message: responseData.message,
                success: responseData.status === true ||
                    responseData.status === "true" ||
                    responseData.status === 200 ||
                    responseData.status === 201,
            };
        }

        return {
            data: responseData,
            code: 200,
            status: true,
            message: "",
            success: true,
        };
    }

    // If payload has data property (successful response)
    if (payload?.data) {
        const responseData = payload.data;
        
        // Check for the new response format: {code: 200, status: true/false, message: "", data: {}}
        if (responseData.code !== undefined) {
            // This is the new format
            const isSuccess = responseData.status === true && 
                             responseData.code === 200 &&
                             (!responseData.data?.errors || responseData.data.errors.length === 0);
            
            return {
                data: responseData.data,
                code: responseData.code,
                status: responseData.status,
                message: responseData.message,
                success: isSuccess
            };
        }
        
        // Old format handling
        return {
            data: responseData.data || responseData,
            status: responseData.status,
            message: responseData.message,
            success: responseData.status === true || 
                   responseData.status === "true" || 
                   responseData.status === 200 || 
                   responseData.status === 201
        };
    }
    
    // If payload has response property (error response)
    if (payload?.response?.data) {
        const errorData = payload.response.data;
        
        if (errorData.code !== undefined) {
            return {
                data: errorData.data,
                code: errorData.code,
                status: errorData.status,
                message: errorData.message,
                success: false
            };
        }
        
        return {
            data: errorData.data || null,
            status: errorData.status,
            message: errorData.message,
            success: false
        };
    }
    
    // Default
    return {
        data: null,
        code: 500,
        status: false,
        message: "Invalid response format",
        success: false
    };
};

// Helper to check if response indicates business logic failure
const isBusinessLogicFailure = (response) => {
    // Check if it's a 200 response but with errors in data
    if (response.code === 200 && response.status === true) {
        // Check if there are errors in the data
        if (response.data?.errors && response.data.errors.length > 0) {
            return true;
        }
        // Check if processedCount is 0 when totalRequested > 0
        if (response.data?.processedCount === 0 && response.data?.totalRequested > 0) {
            return true;
        }
    }
    return false;
};


// Get Review Queue Dashboard Data - WITHOUT action parameter for viewing
// Updated Thunk from tdsSlice.js (using POST as it works in Postman, with fixed spellings and return statements)
export const getReviwQueueDeshboard = createAsyncThunk(
  "tds/getReviwQueueDeshboard",
  async (body, { rejectWithValue }) => {
    try {
      const { path, data = {} } = body;

      if (!path) {
        return rejectWithValue({
          code: 400,
          status: false,
          message: "API path is missing"
        });
      }

      const url = `${baseUrl}${path}`;

      console.log("=== GET REVIEW QUEUE DASHBOARD API ===");
      console.log("Final URL:", url);
      console.log("Request Data:", data);

      if (isViewOnlyAccount()) {
        return getViewOnlyErrorResponse();
      }

      const requestData = {
        ...data,
        userId: data.userId || getUserId(),
        staffId: data.staffId || data.staff_id || data.employee_id,
        financialYear: data.financialYear || data.financial_year
      };

      console.log("Final Request Payload (matching Postman):", requestData);

      const response = await axios.post(url, requestData, config);

      return response.data;

    } catch (error) {
      console.error("getReviwQueueDeshboard API Error:", error);

      if (error.response?.data) {
        console.log("Error response from server:", error.response.data);
        return rejectWithValue(error.response.data);
      }

      return rejectWithValue({
        code: 500,
        status: false,
        message: error.message || "Network error"
      });
    }
  }
);

export const getTdsCalculation = createAsyncThunk(
    'tds/getTdsCalculation',
    async (payload, { rejectWithValue }) => {
        try {
            console.log("=== getTdsCalculation API Call ===");
            console.log("Payload:", payload);
            
            const token = localStorage.getItem('token') 
                || localStorage.getItem('access_token')
                || localStorage.getItem('accessToken');
            
            if (!token) {
                throw new Error("No authentication token found");
            }

            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            };

            const response = await axios.post(payload.path, payload.data, config);
            console.log("API Response:", response.data);
            return response.data;
            
        } catch (error) {
            console.error("getTdsCalculation Error:", error);
            return rejectWithValue(error.response?.data || { message: error.message });
        }
    }
);

const TdsSlice = createSlice({
    name: "tds",
    initialState,
    reducers: {
        // Initialize TDS state
        initTdsState: (state) => {
            state.tdsLoading = false;
            state.tdsSuccess = false;
            state.tdsError = false;
            state.tdsMessage = null;
            state.tdsData = null;
        },
        
        // Initialize TDS Status state
        initStatusState: (state) => {
            state.tdsStatusLoading = false;
            state.tdsStatusSuccess = false;
            state.tdsStatusError = false;
            state.tdsStatusMessage = null;
            state.tdsStatusData = null;
        },
        
        // Initialize TDS General Rules state
        initGenRules: (state) => {
            state.tdsGenLoading = false;
            state.tdsGenSuccess = false;
            state.tdsGenError = false;
            state.tdsGenMessage = null;
            state.tdsGenData = null;
        },
        
        // Initialize TDS Validation state
        initValiRules: (state) => {
            state.tdsValiLoading = false;
            state.tdsValiSuccess = false;
            state.tdsValiError = false;
            state.tdsValiMessage = null;
            state.tdsValiData = null;
        },
        
        // Initialize TDS Notification state
        initNotiRules: (state) => {
            state.tdsNotiLoading = false;
            state.tdsNotiSuccess = false;
            state.tdsNotiError = false;
            state.tdsNotiMessage = null;
            state.tdsNotiData = null;
        },
        
        // Initialize All TDS state
        initAllTds: (state) => {
            state.tdsAllLoading = false;
            state.tdsAllSuccess = false;
            state.tdsAllError = false;
            state.tdsAllMessage = null;
            state.tdsAllData = null;
        },
        
        // Initialize TDS Employee List state
        initTdsEmpList: (state) => {
            state.tdsEmpListLoading = false;
            state.tdsEmpListSuccess = false;
            state.tdsEmpListError = false;
            state.tdsEmpListMessage = null;
            state.tdsEmpListData = null;
        },
        
        // Initialize Declaration List state
        initDecList: (state) => {
            state.tdsDeclLoading = false;
            state.tdsDeclSuccess = false;
            state.tdsDeclError = false;
            state.tdsDeclMessage = null;
            state.tdsDeclData = null;
        },
        
        // Reset all TDS states
        resetAllTds: (state) => {
            Object.keys(initialState).forEach(key => {
                state[key] = initialState[key];
            });
        },
        
        // Clear specific error message
        clearTdsStatusError: (state) => {
            state.tdsStatusError = false;
            state.tdsStatusMessage = null;
        },
        
        // Debug helper to check user data
        debugUserData: (state) => {
            console.log("Debug - User data:", getUserData());
            console.log("Debug - User ID:", getUserId());
            console.log("Debug - Account type:", getAccountType());
            console.log("Debug - Is view-only:", isViewOnlyAccount());
        }
    },
    extraReducers: (builder) => {
        // -------------------------------------------------
        // saveTdsStatusData reducers
        builder.addCase(saveTdsStatusData.pending, (state) => {
            state.tdsStatusLoading = true;
            state.tdsStatusSuccess = false;
            state.tdsStatusError = false;
            state.tdsStatusMessage = null;
            state.tdsStatusData = null;
        });
        
        builder.addCase(saveTdsStatusData.fulfilled, (state, action) => {
            const isViewOnly = isViewOnlyAccount();
            const response = extractResponseData(action);
            
            console.log("saveTdsStatusData response:", response);
            
            // Check for business logic failure (200 status but errors in data)
            const hasBusinessError = isBusinessLogicFailure(response);
            
            if (response.success && !hasBusinessError) {
                // True success - HTTP 200 AND no business logic errors
                state.tdsStatusLoading = false;
                state.tdsStatusSuccess = isViewOnly ? false : true;
                state.tdsStatusError = isViewOnly ? true : false;
                state.tdsStatusMessage = isViewOnly ? 
                    "Sorry, you can only view the data, not create it." : 
                    response.message || "Status saved successfully";
                state.tdsStatusData = response.data;
            } else {
                // Either HTTP error or business logic error
                state.tdsStatusLoading = false;
                state.tdsStatusSuccess = false;
                state.tdsStatusError = true;
                
                // Provide detailed error message
                if (hasBusinessError) {
                    // Business logic error (like "TDS declaration not found")
                    state.tdsStatusMessage = response.message || 
                        (response.data?.errors?.join(", ") || "Operation failed");
                } else {
                    // HTTP or other error
                    state.tdsStatusMessage = response.message || "Failed to save status";
                }
                
                state.tdsStatusData = response.data;
            }
        });
        
        builder.addCase(saveTdsStatusData.rejected, (state, action) => {
            state.tdsStatusLoading = false;
            state.tdsStatusSuccess = false;
            state.tdsStatusError = true;
            state.tdsStatusMessage = action.error?.message || "Request failed";
            state.tdsStatusData = null;
        });

        
        
        // -------------------------------------------------
        // getTdsAllData reducers
        builder.addCase(getTdsAllData.pending, (state) => {
            state.tdsAllLoading = true;
            state.tdsAllSuccess = false;
            state.tdsAllError = false;
            state.tdsAllMessage = null;
            state.tdsAllData = null;
        });
        
        builder.addCase(getTdsAllData.fulfilled, (state, action) => {
            const isViewOnly = isViewOnlyAccount();
            const response = extractResponseData(action);
            
            if (response.success) {
                state.tdsAllLoading = false;
                state.tdsAllSuccess = isViewOnly ? false : true;
                state.tdsAllError = isViewOnly ? true : false;
                state.tdsAllMessage = isViewOnly ? 
                    "Sorry, you can only view the data, not create it." : 
                    response.message;
                state.tdsAllData = response.data;
            } else {
                state.tdsAllLoading = false;
                state.tdsAllSuccess = false;
                state.tdsAllError = true;
                state.tdsAllMessage = response.message;
                state.tdsAllData = null;
            }
        });
        
        builder.addCase(getTdsAllData.rejected, (state, action) => {
            state.tdsAllLoading = false;
            state.tdsAllSuccess = false;
            state.tdsAllError = true;
            state.tdsAllMessage = action.error?.message || "Request failed";
            state.tdsAllData = null;
        });
        
        // -------------------------------------------------
        // getTdsEmpListData reducers
        builder.addCase(getTdsEmpListData.pending, (state) => {
            state.tdsEmpListLoading = true;
            state.tdsEmpListSuccess = false;
            state.tdsEmpListError = false;
            state.tdsEmpListMessage = null;
            state.tdsEmpListData = null;
        });
        
        builder.addCase(getTdsEmpListData.fulfilled, (state, action) => {
            const isViewOnly = isViewOnlyAccount();
            const response = extractResponseData(action);
            // Debug logging to help trace payload issues
            console.log("getTdsEmpListData.fulfilled action.payload:", action.payload);
            console.log("Parsed response:", response);

            // Primary success path
            // Normalize employees array from various response shapes
            const resolvedData = response.data;
            let employeesArray = [];

            if (Array.isArray(resolvedData)) {
                employeesArray = resolvedData;
            } else if (resolvedData) {
                // common shapes
                employeesArray = resolvedData.employeeList?.employees || resolvedData.employees || resolvedData.data?.employeeList?.employees || resolvedData.data?.employees || [];
            }

            // Assign normalized data and set flags
            state.tdsEmpListLoading = false;
            state.tdsEmpListData = resolvedData || null;
            state.tdsEmpListNormalized = employeesArray;

            if (employeesArray.length > 0) {
                state.tdsEmpListSuccess = isViewOnly ? false : true;
                state.tdsEmpListError = isViewOnly ? true : false;
                state.tdsEmpListMessage = isViewOnly ? "Sorry, you can only view the data, not create it." : response.message || null;
            } else if (response.success) {
                // success but empty result
                state.tdsEmpListSuccess = true;
                state.tdsEmpListError = false;
                state.tdsEmpListMessage = response.message || null;
            } else {
                state.tdsEmpListSuccess = false;
                state.tdsEmpListError = true;
                state.tdsEmpListMessage = response.message || null;
            }
        });
        
        builder.addCase(getTdsEmpListData.rejected, (state, action) => {
            state.tdsEmpListLoading = false;
            state.tdsEmpListSuccess = false;
            state.tdsEmpListError = true;
            state.tdsEmpListMessage = action.error?.message || "Request failed";
            state.tdsEmpListData = null;
        });
        
        // -------------------------------------------------
        // getTdsDecListData reducers
        builder.addCase(getTdsDecListData.pending, (state) => {
            state.tdsDeclLoading = true;
            state.tdsDeclSuccess = false;
            state.tdsDeclError = false;
            state.tdsDeclMessage = null;
            state.tdsDeclData = null;
        });
        
        builder.addCase(getTdsDecListData.fulfilled, (state, action) => {
            const isViewOnly = isViewOnlyAccount();
            const response = extractResponseData(action);
            
            if (response.success) {
                state.tdsDeclLoading = false;
                state.tdsDeclSuccess = isViewOnly ? false : true;
                state.tdsDeclError = isViewOnly ? true : false;
                state.tdsDeclMessage = isViewOnly ? 
                    "Sorry, you can only view the data, not create it." : 
                    response.message;
                state.tdsDeclData = response.data;
            } else {
                state.tdsDeclLoading = false;
                state.tdsDeclSuccess = false;
                state.tdsDeclError = true;
                state.tdsDeclMessage = response.message;
                state.tdsDeclData = null;
            }
        });
        
        builder.addCase(getTdsDecListData.rejected, (state, action) => {
            state.tdsDeclLoading = false;
            state.tdsDeclSuccess = false;
            state.tdsDeclError = true;
            state.tdsDeclMessage = action.error?.message || "Request failed";
            state.tdsDeclData = null;
        });

        builder.addCase(getReviwQueueDeshboard.pending, (state) => {
            state.reviewQueueLoading = true;
            state.reviewQueueSuccess = false;
            state.reviewQueueError = false;
            state.reviewQueueMessage = null;
        });
        builder.addCase(getReviwQueueDeshboard.fulfilled, (state, action) => {
            const response = action.payload || {};
            const success = response.status === true || response.code === 200;
            state.reviewQueueLoading = false;
            state.reviewQueueSuccess = success;
            state.reviewQueueError = !success;
            state.reviewQueueMessage = response.message || null;
            state.reviewQueueData = response.data || response;
        });
        builder.addCase(getReviwQueueDeshboard.rejected, (state, action) => {
            state.reviewQueueLoading = false;
            state.reviewQueueSuccess = false;
            state.reviewQueueError = true;
            state.reviewQueueMessage = action.payload?.message || action.error?.message || "Request failed";
        });
        
        // -------------------------------------------------
        // tdsSaveData reducers
        builder.addCase(tdsSaveData.pending, (state) => {
            state.tdsLoading = true;
            state.tdsSuccess = false;
            state.tdsError = false;
            state.tdsMessage = null;
            state.tdsData = null;
        });
        
        builder.addCase(tdsSaveData.fulfilled, (state, action) => {
            const isViewOnly = isViewOnlyAccount();
            const response = extractResponseData(action);
            const hasBusinessError = isBusinessLogicFailure(response);
            
            if (response.success && !hasBusinessError) {
                state.tdsLoading = false;
                state.tdsSuccess = isViewOnly ? false : true;
                state.tdsError = isViewOnly ? true : false;
                state.tdsMessage = isViewOnly ? 
                    "Sorry, you can only view the data, not create it." : 
                    response.message;
                state.tdsData = response.data;
            } else {
                state.tdsLoading = false;
                state.tdsSuccess = false;
                state.tdsError = true;
                state.tdsMessage = hasBusinessError ? 
                    (response.data?.errors?.join(", ") || response.message) : 
                    response.message;
                state.tdsData = null;
            }
        });
        
        builder.addCase(tdsSaveData.rejected, (state, action) => {
            state.tdsLoading = false;
            state.tdsSuccess = false;
            state.tdsError = true;
            state.tdsMessage = action.error?.message || "Request failed";
            state.tdsData = null;
        });
        
        // -------------------------------------------------
        // getTdsGeneralRules reducers
        builder.addCase(getTdsGeneralRules.pending, (state) => {
            state.tdsGenLoading = true;
            state.tdsGenSuccess = false;
            state.tdsGenError = false;
            state.tdsGenMessage = null;
            state.tdsGenData = null;
        });
        
        builder.addCase(getTdsGeneralRules.fulfilled, (state, action) => {
            const isViewOnly = isViewOnlyAccount();
            const response = extractResponseData(action);
            
            if (response.success) {
                state.tdsGenLoading = false;
                state.tdsGenSuccess = isViewOnly ? false : true;
                state.tdsGenError = isViewOnly ? true : false;
                state.tdsGenMessage = isViewOnly ? 
                    "Sorry, you can only view the data, not create it." : 
                    response.message;
                state.tdsGenData = response.data;
            } else {
                state.tdsGenLoading = false;
                state.tdsGenSuccess = false;
                state.tdsGenError = true;
                state.tdsGenMessage = response.message;
                state.tdsGenData = null;
            }
        });
        
        builder.addCase(getTdsGeneralRules.rejected, (state, action) => {
            state.tdsGenLoading = false;
            state.tdsGenSuccess = false;
            state.tdsGenError = true;
            state.tdsGenMessage = action.error?.message || "Request failed";
            state.tdsGenData = null;
        });
        
        // -------------------------------------------------
        // getTdsValidation reducers
        builder.addCase(getTdsValidation.pending, (state) => {
            state.tdsValiLoading = true;
            state.tdsValiSuccess = false;
            state.tdsValiError = false;
            state.tdsValiMessage = null;
            state.tdsValiData = null;
        });
        
        builder.addCase(getTdsValidation.fulfilled, (state, action) => {
            const isViewOnly = isViewOnlyAccount();
            const response = extractResponseData(action);
            
            if (response.success) {
                state.tdsValiLoading = false;
                state.tdsValiSuccess = isViewOnly ? false : true;
                state.tdsValiError = isViewOnly ? true : false;
                state.tdsValiMessage = isViewOnly ? 
                    "Sorry, you can only view the data, not create it." : 
                    response.message;
                state.tdsValiData = response.data;
            } else {
                state.tdsValiLoading = false;
                state.tdsValiSuccess = false;
                state.tdsValiError = true;
                state.tdsValiMessage = response.message;
                state.tdsValiData = null;
            }
        });
        
        builder.addCase(getTdsValidation.rejected, (state, action) => {
            state.tdsValiLoading = false;
            state.tdsValiSuccess = false;
            state.tdsValiError = true;
            state.tdsValiMessage = action.error?.message || "Request failed";
            state.tdsValiData = null;
        });
        
        // -------------------------------------------------
        // getTdsNotification reducers
        builder.addCase(getTdsNotification.pending, (state) => {
            state.tdsNotiLoading = true;
            state.tdsNotiSuccess = false;
            state.tdsNotiError = false;
            state.tdsNotiMessage = null;
            state.tdsNotiData = null;
        });
        
        builder.addCase(getTdsNotification.fulfilled, (state, action) => {
            const isViewOnly = isViewOnlyAccount();
            const response = extractResponseData(action);
            
            if (response.success) {
                state.tdsNotiLoading = false;
                state.tdsNotiSuccess = isViewOnly ? false : true;
                state.tdsNotiError = isViewOnly ? true : false;
                state.tdsNotiMessage = isViewOnly ? 
                    "Sorry, you can only view the data, not create it." : 
                    response.message;
                state.tdsNotiData = response.data;
            } else {
                state.tdsNotiLoading = false;
                state.tdsNotiSuccess = false;
                state.tdsNotiError = true;
                state.tdsNotiMessage = response.message;
                state.tdsNotiData = null;
            }
        });
        
        builder.addCase(getTdsNotification.rejected, (state, action) => {
            state.tdsNotiLoading = false;
            state.tdsNotiSuccess = false;
            state.tdsNotiError = true;
            state.tdsNotiMessage = action.error?.message || "Request failed";
            state.tdsNotiData = null;
        });
    },
});

// Export actions
export const { 
    initTdsState, 
    initStatusState, 
    initGenRules, 
    initValiRules, 
    initNotiRules, 
    initAllTds, 
    initTdsEmpList, 
    initDecList,
    resetAllTds,
    clearTdsStatusError,
    debugUserData
} = TdsSlice.actions;

// Export reducer
export default TdsSlice.reducer;
